import { Component, Input, forwardRef, ViewEncapsulation, ViewChild, ElementRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, NG_VALIDATORS, Validator, AbstractControl } from '@angular/forms';
import { SabisTranslateService } from '../translation/sdp-translate.service';
import { WritingMode } from './writing-mode.directive';

@Component({
  selector: 'sabis-ngx-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SabisInput2Component),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SabisInput2Component),
      multi: true,
    }
  ]
})
export class SabisInput2Component implements ControlValueAccessor, Validator {
  @Input('scTabindex') scTabindex: number | undefined;
  @Input('scType') scType!: string;
  @Input('scPlaceholder') scPlaceholder: string = '';
  @Input('scLabel') scLabel: string | null = null;
  @Input('scMax') scMax: number | undefined;
  @Input('scMin') scMin: number | undefined;
  @Input('scPattern') scPattern: string = '';
  @Input('scPatternValidationMessage') scPatternValidationMessage: string = '';
  @Input('scDecimalPlaces') scDecimalPlaces: number = 0;
  @Input('scHideValidationMessage') scHideValidationMessage: boolean = false;
  @Input('scAllowWhiteSpace') scAllowWhiteSpace: boolean = false;
  @Input('scFocusOnInit') scFocusOnInit: boolean = false;
  @Input('scShowClearButton') scShowClearButton: boolean = true;
  @Input('scWritingMode') scWritingMode: {
    /** the writing mode */
    mode: WritingMode,
    /** string considered to set first letter after to upper case
     *  default it's space */
    splitter?: string
  } | undefined;

  @ViewChild('scInput', { static: true }) scInput!: ElementRef;

  public isDisabled: boolean = false;
  public hasFocus: boolean = false;
  public isClearBtnVisible: boolean = false;

  public isValid: boolean = true;
  public validationMessage: string = '';

  private _innerValue: string | number = '';

  private onChangeCallback: null | ((v: string | number) => void) = null;
  private onTouchedCallback: null | ((e: any) => void) = null;


  get value() {
    return this._innerValue;
  }
  set value(v: any) {
    if (this._innerValue !== v) {
      if (this.scType == "number" && v) {
        v = Number(v);
      }
      else if (this.scType == "text" && v && !this.scAllowWhiteSpace) {
        v = v.trim();
      }
      this._innerValue = v;
      this.isClearBtnVisible = this._innerValue != '' ? true : false;
    }
  }

  constructor(private _translateService: SabisTranslateService) { }

  ngOnInit() {
    if (this.scFocusOnInit) {
      this.focus();
    }
  }

  isRequired(): boolean {
    return false;
  }

  onFocus(event: any) {
    this.hasFocus = true;
    this.isClearBtnVisible = this.value != '' ? true : false;
  }


  /**
  * Handler for model change 
  * @param event
  */
  onModelChange(event: any) {
    this.value = this.changeTextCase(event);
    if (this.onChangeCallback) {
      this.onChangeCallback(this._innerValue);
    }
  }

  /**
   * hide the x unless the focus is triggered by the clear button
   * change text display mode 
   * @param e
   */
  onFocusOut(e: any) {
    if (!(e.relatedTarget && e.relatedTarget.className.search('input-clear') > -1)) {
      this.isClearBtnVisible = false;
      this.hasFocus = false;
    }

    if (this.value && this.scType === 'number' && this.scDecimalPlaces && this.scDecimalPlaces != 0) {
      this.value = Number(parseFloat(this.value).toFixed(this.scDecimalPlaces));
    }
  }

  onBlur(event: any) {
    if (this.onTouchedCallback) {
      this.onTouchedCallback(event);
    }
  }

  /**
   * prevent decimal entry
   * @param event
   */
  onKeyPress(event: any) {
    if (this.scType === 'number') {
      // '-' or any number
      var isValid = (event.charCode == 45) || (event.charCode >= 48 && event.charCode <= 57);
      if (this.scDecimalPlaces || this.scDecimalPlaces != 0) {
        isValid = isValid || event.charCode == 46; // number and decimal
      }
      return isValid;
    }
    return true;
  }



  onClearClick() {
    this.value = '';
    this.isClearBtnVisible = false;
    this.scInput.nativeElement.focus();
  }

  writeValue(obj: any): void {
      this.value = obj;
  }
  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  focus() {
    if (this.scInput.nativeElement) {
      this.scInput.nativeElement.focus();
    }
  }

  validate(c: AbstractControl): { [key: string]: any; } {
    this.setValidationMessage(c);
    let result = {};
    // Add minValue and maxValue custom  validation
    var value = c.value;// this.scInput.nativeElement.value;
    if (this.scType === 'number' && !isNaN(parseFloat(value))) {
      value = parseFloat(value);
      if (value != null && this.scMax && value > this.scMax) {
        result = {
          maxValue: {
            valid: false,
            requiredValue: this.scMax,
            actualValue: value
          },
        };
      }
      if (value != null && this.scMin && value < this.scMin) {
        result = {
          minValue: {
            valid: false,
            requiredValue: this.scMin,
            actualValue: value
          },
        };
      }
    }
    if (this.scPattern && !(new RegExp(this.scPattern).test(value))) {
      result = {
        pattern: {
          valid: false
        },
      };
    }
    return result;

  }
  registerOnValidatorChange?(fn: () => void): void {
  }

  private setValidationMessage(c: AbstractControl): void {
    //set Time out because the control changes validation after passing in the validate function
    setTimeout(() => {
      this.isValid = c.errors == null;
      if (!this.isValid) {
        for (var e in c.errors) {
          this.validationMessage = c.errors[e];
          if (e == "required") {
            this.validationMessage = this._translateService.instant("SabisComponents.FieldRequired");
          }
          else if (e == "maxlength") {
            this.validationMessage = this._translateService.instant("SabisComponents.MaxLengthIs") + " " + c.errors['maxlength'].requiredLength;
          }
          else if (e == "maxValue") {
            this.validationMessage = this._translateService.instant("SabisComponents.MaxValueIs") + " " + c.errors['maxValue'].requiredValue;
          }
          else if (e == "minValue") {
            this.validationMessage = this._translateService.instant("SabisComponents.MinValueIs") + " " + c.errors['minValue'].requiredValue;
          }
          else
            if (e == "pattern") {
              this.validationMessage = this.scPatternValidationMessage;
            }
            else {
              this.validationMessage = "";
            }
        }
      }
    });
  }

  /**
  * will change the value accoriding to the passed writing mode
  * @param value
  */
  private changeTextCase(value: string): string {
    if (value && this.scType === 'text' && this.scWritingMode) {
      if (this.scWritingMode.mode === WritingMode.UpperCase) {
        value = value.toUpperCase();
      }

      if (this.scWritingMode.mode === WritingMode.LowerCase) {
        value = value.toLowerCase();
      }

      if (this.scWritingMode.mode === WritingMode.FirstLetterCaps) {
        let splitter = this.scWritingMode.splitter ? this.scWritingMode.splitter : ' ';
        // set first letter to Caps after all available splitter
        value = this.capitalizeFirstLetter(value, splitter);
      }
    }
    return value;
  }

  /**
  * will capitzalize the first letter of the sentence. and after each splitters 
  * @param str
  * @param splitters
  */
  private capitalizeFirstLetter(str: string, splitter: string): string {
    let splitStr = str.toLowerCase().split(splitter);
    for (var j = 0; j < splitStr.length; j++) {
      splitStr[j] = splitStr[j].charAt(0).toUpperCase() + splitStr[j].substring(1);
    }
    // return the joined string
    return splitStr.join(splitter);
  }

  /**
  * will return the passed string in camel case
  * @param str
  */
  private toCamelCase(str: string): string {
    return str
      .replace(/\s(.)/g, function (v) { return v.toUpperCase(); })
      .replace(/\s/g, '')
      .replace(/^(.)/, function (v) { return v.toLowerCase(); });
  }

  private toPascalCase(str: string): string {
    return str.replace(/(\w)(\w*)/g, function (g0, g1, g2) { return g1.toUpperCase() + g2.toLowerCase(); });
  }

}
